import React, { createContext, useContext, useState, useEffect } from 'react';
import { Project } from '../types';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';

interface ProjectContextType {
  projects: Project[];
  selectedProject: Project | null;
  setSelectedProject: (project: Project | null) => void;
  selectProjectWithValidation: (project: Project | null) => void;
  refreshProjects: () => Promise<void>;
  loading: boolean;
}

const ProjectContext = createContext<ProjectContextType | undefined>(undefined);

export const useProjectContext = () => {
  const context = useContext(ProjectContext);
  if (!context) throw new Error('useProjectContext must be used within ProjectProvider');
  return context;
};

export const ProjectProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { logout, user } = useAuth();

  // localStorage utilities
  const getStoredProjectId = (userId: string): string | null => {
    return localStorage.getItem(`selectedProject_${userId}`);
  };

  const setStoredProjectId = (userId: string, projectId: string): void => {
    localStorage.setItem(`selectedProject_${userId}`, projectId);
  };

  const selectProjectWithValidation = (project: Project | null): void => {
    setSelectedProject(project);
    if (project && user?.id) {
      setStoredProjectId(user.id, project.id);
    }
  };

  const fetchProjects = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch('/api/projects', {
        credentials: 'include',
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        }
      });
      if (res.status === 401) {
        logout();
        navigate('/login');
        return;
      }
      const data = await res.json();
      if (data.success && Array.isArray(data.data)) {
        setProjects(data.data);
        
        // Enhanced selection logic
        if (data.data.length === 1) {
          // Single project: auto-select
          selectProjectWithValidation(data.data[0]);
        } else if (data.data.length > 1) {
          // Multiple projects: check localStorage
          const storedProjectId = user?.id ? getStoredProjectId(user.id) : null;
          if (storedProjectId) {
            // Find stored project in user's project list
            const storedProject = data.data.find((p: Project) => p.id === storedProjectId);
            if (storedProject) {
              selectProjectWithValidation(storedProject);
            } else {
              // Stored project not found, select latest
              selectProjectWithValidation(data.data[0]);
            }
          } else {
            // No stored project, select latest
            selectProjectWithValidation(data.data[0]);
          }
        }
      }
    } catch (err) {
      setProjects([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProjects();
  }, []);

  return (
    <ProjectContext.Provider value={{ projects, selectedProject, setSelectedProject, selectProjectWithValidation, refreshProjects: fetchProjects, loading }}>
      {children}
    </ProjectContext.Provider>
  );
}; 